import React, { useEffect, useState } from "react";
import { db } from "../firebaseConfig";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  doc,
} from "firebase/firestore";
import {
  Card,
  Input,
  Modal,
  Button,
  Form,
  Upload,
  List,
  Tag,
  message,
  Space,
  Typography,
  Divider,
  Row,
  Col,
  Tooltip,
  Skeleton,
} from "antd";
import {
  UploadOutlined,
  PlusOutlined,
  LikeOutlined,
  LikeFilled,
  CopyOutlined,
} from "@ant-design/icons";

const { Title, Paragraph } = Typography;

const CommonIssues = () => {
  const [issues, setIssues] = useState([]);
  const [filteredIssues, setFilteredIssues] = useState([]);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTag, setActiveTag] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uniqueTags, setUniqueTags] = useState([]);
  const [upvotedIssues, setUpvotedIssues] = useState(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchIssues = async () => {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, "common_issues"));
      const data = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setIssues(data);
      setFilteredIssues(data);
      extractTags(data);
      setLoading(false);
    };
    fetchIssues();
  }, []);

  const extractTags = (issuesList) => {
    const tagSet = new Set();
    issuesList.forEach((issue) => {
      if (issue.tags) {
        issue.tags.forEach((tag) => tagSet.add(tag.trim().toLowerCase()));
      }
    });
    setUniqueTags([...tagSet]);
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    filterIssues(term, activeTag);
  };

  const handleTagFilter = (tag) => {
    const newActiveTag = tag === activeTag ? null : tag;
    setActiveTag(newActiveTag);
    filterIssues(searchTerm, newActiveTag);
  };

  const filterIssues = (term, tag) => {
    if (Array.isArray(issues)) {
      const filtered = issues.filter((issue) => {
        const lowerTags = Array.isArray(issue.tags)
          ? issue.tags.map((t) => t.toLowerCase())
          : [];
        const tagMatch = !tag || lowerTags.includes(tag.toLowerCase());
        const content = `${issue.title} ${issue.description} ${issue.symptoms} ${issue.resolution}`.toLowerCase();
        return content.includes(term.toLowerCase()) && tagMatch;
      });
      setFilteredIssues(filtered);
    }
  };

  const handleSubmit = async (values) => {
    const tagArray = values.tags
      ? values.tags.split(",").map((tag) => tag.trim().toLowerCase())
      : [];

    const newIssue = {
      ...values,
      tags: tagArray,
      image: uploadedImage || "",
      createdAt: new Date(),
      upvotes: 0,
    };

    await addDoc(collection(db, "common_issues"), newIssue);
    message.success("Issue logged successfully!");
    form.resetFields();
    setUploadedImage(null);
    setIsModalOpen(false);

    const updatedIssues = await getDocs(collection(db, "common_issues"));
    const data = updatedIssues.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    setIssues(data);
    setFilteredIssues(data);
    extractTags(data);
  };

  const handleUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setUploadedImage(e.target.result);
    };
    reader.readAsDataURL(file);
    return false;
  };

  const handleUpvote = async (issue) => {
    if (upvotedIssues.has(issue.id)) {
      message.info("You've already upvoted this issue.");
      return;
    }

    const updatedUpvotes = (issue.upvotes || 0) + 1;
    const issueRef = doc(db, "common_issues", issue.id);

    await updateDoc(issueRef, { upvotes: updatedUpvotes });

    const updatedIssues = issues.map((i) =>
      i.id === issue.id ? { ...i, upvotes: updatedUpvotes } : i
    );
    setIssues(updatedIssues);
    setFilteredIssues(updatedIssues);
    setUpvotedIssues(new Set([...upvotedIssues, issue.id]));

    if (selectedIssue?.id === issue.id) {
      setSelectedIssue({ ...issue, upvotes: updatedUpvotes });
    }
  };

  const copyResolution = (text) => {
    navigator.clipboard.writeText(text);
    message.success("Resolution copied to clipboard");
  };

  return (
    <div
      style={{
        padding: "40px",
        backgroundColor: "#f0f2f5",
        minHeight: "100vh",
      }}
    >
      <Title level={4} style={{ textAlign: "center", marginBottom: 20 }}>
        🛠️ Common Issues
      </Title>

      {/* Filters and Actions */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 30 }}>
        <Col xs={24} md={12} style={{ marginBottom: 16 }}>
          <Space wrap>
            {uniqueTags.map((tag) => (
              <Tag
                key={tag}
                color={tag === activeTag ? "geekblue" : "blue"}
                style={{
                  cursor: "pointer",
                  fontSize: 14,
                  padding: "5px 12px",
                }}
                onClick={() => handleTagFilter(tag)}
              >
                {tag}
              </Tag>
            ))}
            {activeTag && (
              <Button size="small" onClick={() => handleTagFilter(null)}>
                Clear Filters
              </Button>
            )}
          </Space>
        </Col>
        <Col xs={24} md={12} style={{ textAlign: "right" }}>
          <Space>
            <Input.Search
              placeholder="Search issues"
              value={searchTerm}
              onChange={handleSearch}
              allowClear
              style={{ width: 240 }}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsModalOpen(true)}
            >
              Log New Issue
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Loader / Issues */}
      {loading ? (
        <div
          style={{
            textAlign: "center",
            paddingTop: "60px",
            minHeight: "60vh",
            position: "relative",
          }}
        >
          <div
            className="loader"
            style={{
              border: "6px solid #f3f3f3",
              borderTop: "6px solid #1890ff",
              borderRadius: "50%",
              width: "60px",
              height: "60px",
              margin: "0 auto",
              animation: "spin 1s linear infinite",
            }}
          ></div>
          <Paragraph
            type="secondary"
            style={{ marginTop: 16, fontSize: 16, color: "#555" }}
          >
            🔍 Fetching issues from the knowledge base...
          </Paragraph>

          <Row gutter={20} style={{ marginTop: 40 }}>
            {[...Array(6)].map((_, i) => (
              <Col xs={24} sm={12} md={8} key={i}>
                <Card style={{ height: 250, borderRadius: 10 }}>
                  <Skeleton active paragraph={{ rows: 3 }} />
                </Card>
              </Col>
            ))}
          </Row>

          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
      ) : (
        <List
          grid={{ gutter: 20, column: 3 }}
          dataSource={filteredIssues}
          locale={{ emptyText: "No issues found." }}
          renderItem={(issue) => (
            <List.Item>
              <Card
                hoverable
                onClick={() => setSelectedIssue(issue)}
                title={<b>{issue.title}</b>}
                style={{
                  borderRadius: 10,
                  height: 250,
                  overflow: "hidden",
                  transition:
                    "transform 0.3s ease, box-shadow 0.3s ease",
                }}
                bodyStyle={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  height: "100%",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.transform = "scale(1.02)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.transform = "scale(1)")
                }
              >
                <Paragraph ellipsis={{ rows: 3 }}>
                  {issue.description}
                </Paragraph>
                <div>
                  <Space wrap>
                    {issue.tags?.slice(0, 2).map((tag, index) => (
                      <Tag key={index}>{tag}</Tag>
                    ))}
                    {issue.tags?.length > 2 && (
                      <Tag>+{issue.tags.length - 2} more</Tag>
                    )}
                  </Space>
                </div>
              </Card>
            </List.Item>
          )}
        />
      )}

      {/* Log New Issue Modal */}
      <Modal
        title="📝 Log New Issue"
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          form.resetFields();
          setUploadedImage(null);
        }}
        footer={null}
        destroyOnClose
      >
        <Form layout="vertical" form={form} onFinish={handleSubmit}>
          <Form.Item
            name="title"
            label="Issue Title"
            rules={[{ required: true, message: "Please enter Issue Title" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: "Please enter Description" }]}
          >
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item
            name="symptoms"
            label="Symptoms"
            rules={[{ required: true, message: "Please enter Symptoms" }]}
          >
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item
            name="resolution"
            label="Resolution (each point in new line)"
            rules={[{ required: true, message: "Please enter Resolution" }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="tags" label="Tags (comma separated)">
            <Input placeholder="e.g. UI, crash, config, COB" />
          </Form.Item>
          <Form.Item name="image" label="Upload Image">
            <Upload
              accept="image/*"
              listType="picture"
              maxCount={1}
              beforeUpload={handleUpload}
              onRemove={() => setUploadedImage(null)}
            >
              <Button icon={<UploadOutlined />}>Upload</Button>
            </Upload>
            {uploadedImage && (
              <img
                src={uploadedImage}
                alt="Preview"
                style={{
                  width: "100%",
                  marginTop: 10,
                  borderRadius: 8,
                }}
              />
            )}
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              Submit
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Issue Details Modal */}
      {selectedIssue && (
        <Modal
          title={
            <Space direction="vertical">
              <Title level={5} style={{ margin: 0 }}>
                {selectedIssue.title}
              </Title>
              <Typography.Text
                type="secondary"
                style={{ fontSize: 12 }}
              >
                Logged on:{" "}
                {selectedIssue.createdAt?.seconds
                  ? new Date(
                      selectedIssue.createdAt.seconds * 1000
                    ).toLocaleString()
                  : "Unknown"}
              </Typography.Text>
            </Space>
          }
          open={!!selectedIssue}
          onCancel={() => setSelectedIssue(null)}
          footer={null}
          width={800}
        >
          <Paragraph>
            <strong>Description:</strong> {selectedIssue.description}
          </Paragraph>
          <Paragraph>
            <strong>Symptoms:</strong> {selectedIssue.symptoms}
          </Paragraph>
          <Divider />
          <div>
            <strong>Resolution:</strong>
            <Button
              size="small"
              icon={<CopyOutlined />}
              style={{ marginLeft: 10 }}
              onClick={() => copyResolution(selectedIssue.resolution)}
            >
              Copy
            </Button>
            <ul style={{ paddingLeft: 20, marginTop: 10 }}>
              {selectedIssue.resolution
                .split("\n")
                .filter((line) => line.trim() !== "")
                .map((point, index) => (
                  <li key={index}>{point}</li>
                ))}
            </ul>
          </div>
          {selectedIssue.tags?.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <strong>Tags:</strong>{" "}
              {selectedIssue.tags.map((tag, index) => (
                <Tag key={index} color="geekblue">
                  {tag}
                </Tag>
              ))}
            </div>
          )}
          <div style={{ marginTop: 20 }}>
            <Tooltip title="Upvote this issue if it's helpful!">
              <Button
                icon={
                  upvotedIssues.has(selectedIssue.id) ? (
                    <LikeFilled />
                  ) : (
                    <LikeOutlined />
                  )
                }
                onClick={() => handleUpvote(selectedIssue)}
              >
                Upvote ({selectedIssue.upvotes || 0})
              </Button>
            </Tooltip>
          </div>
          {selectedIssue.image && (
            <img
              src={selectedIssue.image}
              alt="Issue"
              style={{
                maxWidth: "100%",
                marginTop: 20,
                borderRadius: 10,
                border: "1px solid #ddd",
              }}
            />
          )}
        </Modal>
      )}
    </div>
  );
};

export default CommonIssues;